import { defineStore } from "pinia";
import api from "@/services/axios";
import {
  mergePageParameters,
  applyParametersToPages,
  getEffectivePageParameters,
  DEFAULT_PAGE_PARAMETERS,
} from "@/utils/pageParametersUtils";

function debounce(func, wait) {
  let timeout;
  return function (...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
}

export const useTestStore = defineStore("test", {
  state: () => ({
    testData: {
      _id: "",
      ID: "",
      title: "",
      type: "",
      isNewElement: false,
      version: "",
      language: "",
      navigation: "",
      progression: "",
      Description: "",
      Subject: "",
      location: "",
      ISCED: "",
      level: "",
      domain: "",
      duration: "",
      Keywords: [],
      CATParameters: {
        paliers: 4,
        confirmation: 3,
        echec: 3,
        calculatedItems: 0,
        randomizedItemOrder: true,
      },
      metadata: {
        Author: "",
        Created: "",
        LastModif: "",
      },
      pages: [],
      elements: [],
      dimensions: [],
      settings: {
        timeLimit: false,
        sufflePages: false,
        shuffleQuestions: false,
        shuffleoptions: false,
        NavigationPanel: false,
        progressBar: false,
        feedbackAllowed: false,
        directScore_FB: false,
        countDown: false,
      },
    },
    ConditionalStructure: {
      nodes: [],
      edges: [],
    },
    selectedElement: null,
    selectedElementLabel: null,
    currentPage: null,
    currentTest: null,
    currentElement: null,
    isResetting: false, // Protection contre les boucles de reset
    imageCache: new Map(), // Cache pour éviter de retraiter les mêmes images
    imageValidationErrors: [], // Stockage des erreurs de validation d'images
    _syncInProgress: false, // Protection contre les appels récursifs de syncDifficultiesWithCAT
  }),

  actions: {
    resetState() {
      // Protection contre les appels récursifs
      if (this.isResetting) {
        return;
      }

      this.isResetting = true;

      try {
        this.testData = null; // Réinitialiser testData pour que checkTest() retourne false
        this.selectedElement = null;
        this.selectedElementLabel = null;
        this.currentPage = null;
        this.currentTest = null;
        this.currentElement = null;
        //console.log("Store state has been reset.");
      } finally {
        // Réinitialiser le flag après un délai
        setTimeout(() => {
          this.isResetting = false;
        }, 50);
      }
    },
    toggleQuestionCollapse(elementId) {
      console.log(elementId);
      if (!this.testData || !Array.isArray(this.testData.elements)) {
        console.error("❌ testData.elements non initialisé");
        return;
      }

      // Trouver l'index de l'élément concerné
      const index = this.testData.elements.findIndex(
        (el) => el.el_ID === elementId
      );

      if (index === -1) {
        console.warn(`⚠️ Élément avec ID ${elementId} introuvable`);
        return;
      }

      // Copier et inverser collapsed
      const element = this.testData.elements[index];
      const newCollapsed = !element.collapsed;

      // this.testData.elements[index] = {
      //   ...element,
      //   collapsed: newCollapsed
      // };

      console.log(
        `📉 État collapse modifié pour ${elementId}: ${newCollapsed}`
      );

      // Sauvegarde locale + serveur
      localStorage.setItem("testData", JSON.stringify(this.testData));
      this.saveTestDataToDatabase();
    },
    updateConditionalNode({ id, data }) {
      if (!this.testData.ConditionalStructure) {
        this.testData.ConditionalStructure = { nodes: [], edges: [] };
      }

      // Cherche le node par id
      const index = this.testData.ConditionalStructure.nodes.findIndex(
        (n) => n.id === id
      );

      if (index !== -1) {
        // Fusionne les données du node existant
        this.testData.ConditionalStructure.nodes[index].data = {
          ...this.testData.ConditionalStructure.nodes[index].data,
          ...data,
        };
      } else {
        // Crée le node s’il n’existe pas encore
        this.testData.ConditionalStructure.nodes.push({
          id,
          type: "choice",
          data,
        });
      }

      console.log(`💾 Node ${id} mis à jour avec :`, data);

      localStorage.setItem("testData", JSON.stringify(this.testData));
      this.saveTestDataToDatabase();
    },

    fetchTestDataFromDatabase() {
      return api
        .get(`/tests/get`)
        .then((response) => {
          const data = response.data;
          if (!data._id) {
            console.error(
              "⚠️ Avertissement : `_id` manquant dans les données récupérées."
            );
            return;
          }
          this.testData = { ...data };
        })
        .catch((error) => {
          console.error("Erreur lors de la récupération des données :", error);
        });
    },

    saveTestDataToDatabase: debounce(function () {
      // Protection : ne pas sauvegarder si pas de données valides
      if (!this.testData || !this.testData._id || this.testData._id === "") {
        console.log("⏭️ Skip save: no valid test data", {
          hasTestData: !!this.testData,
          testData_id: this.testData?._id,
          testDataID: this.testData?.ID,
        });
        return Promise.resolve();
      }

      console.log("💾 saveTestDataToDatabase called with:", {
        testId: this.testData._id,
        customId: this.testData.ID,
        title: this.testData.title,
        elementsCount: this.testData.elements?.length || 0,
      });

      const validateData = (data) => {
        // Vos validations existantes
        data.pages = data.pages.filter((page) => !page.isDeleted);
        data.elements = data.elements.filter((el) => !el.isDeleted);

        // Validation des images Base64 pour chaque élément
        data.elements = data.elements.map((element) => {
          return this.cleanInvalidImages(element);
        });

        // Vos validations spécifiques existantes
        data.elements.forEach((element) => {
          switch (element.el_Type) {
            case "order":
              if (!element.labels) element.labels = [];
              if (!element.weight) element.weight = 0;
              break;
            case "shortAnswer":
              if (!element.correctionAxes) element.correctionAxes = [];
              break;
            case "textGaps":
              if (!element.gaps) element.gaps = [];
              if (!element.el_GapsedText) element.el_GapsedText = "";
              break;
            case "textPuzzle":
              if (!element.gaps) element.gaps = [];
              if (!element.dragElements) element.dragElements = [];
              if (!element.el_SourceText) element.el_SourceText = "";
              break;
            // Ajout pour HotSpot
            case "HotSpot":
              if (!element.el_data) element.el_data = {};
              if (!element.el_Data) element.el_Data = [];
              break;
          }
        });
        return data;
      };

      const validatedData = validateData({ ...this.testData });

      console.log('🚀 [testStore] Envoi POST /tests/save avec', validatedData.elements?.length || 0, 'éléments');
      console.log('🚀 [testStore] Element IDs:', validatedData.elements?.map(el => el.el_ID).slice(-10) || []);
      console.log('🚀 [testStore] URL complète:', api.defaults.baseURL + '/tests/save');

      return api
        .post(`/tests/save`, validatedData)
        .then((response) => {
          console.log("✅ Save response:", response.data);
          // Log des statistiques d'images (optionnel)
          const imagesCount = validatedData.elements.filter(
            (el) => el.el_data?.backgroundImage?.base64
          ).length;
          if (imagesCount > 0) {
            console.log(`Test sauvegardé avec ${imagesCount} image(s) Base64`);
          }
          return response.data;
        })
        .catch((error) => {
          console.error("Erreur de sauvegarde :", error);
          throw error;
        });
    }, 1000),

    updateTestData(updatedData) {
      // Vérification : Ne mettre à jour que si le contenu a changé
      const hasChanges =
        JSON.stringify(updatedData) !== JSON.stringify(this.testData);

      if (hasChanges) {
        this.testData = {
          ...this.testData,
          ...updatedData,
          metadata: {
            ...this.testData?.metadata,
            ...(updatedData.metadata || {}),
            LastModif: new Date().toISOString(), // Mettre à jour seulement si nécessaire
          },
          settings: {
            ...this.testData?.settings,
            ...(updatedData.settings || {}),
          },
          CATParameters: {
            ...this.testData?.CATParameters,
            ...(updatedData.CATParameters || {}),
          },
          CATStructure: {
            ...this.testData?.CATStructure,
            ...(updatedData.CATStructure || {}),
          },
          CATDistribution: {
            ...this.testData?.CATDistribution,
            ...(updatedData.CATDistribution || {}),
          },
        };

        // Sauvegarde uniquement si les données ont changé
        this.saveTestDataToDatabase();

        // Ne pas synchroniser automatiquement ici pour éviter les boucles
        // La synchronisation CAT se fera seulement lors de modifications explicites d'éléments
      }
    },

    saveTestData() {
      try {
        api
          .post(`/tests/save`, this.testData)
          .then((response) => {
            // console.log("Sauvegarde réussie :", response.data);
          })
          .catch((error) => {
            console.error("Erreur de sauvegarde :", error);
          });

        localStorage.setItem("testData", JSON.stringify(this.testData));
      } catch (error) {
        console.error(
          "Erreur lors de la sauvegarde dans localStorage :",
          error
        );
      }
    },

    setTestData(data) {
      console.log(
        "🔄 setTestData appelé avec:",
        data?.title || "données vides"
      );
      console.trace("📍 Stack trace de l'appel setTestData"); // Voir qui appelle

      // Éviter les mises à jour inutiles qui créent des boucles
      if (JSON.stringify(this.testData) === JSON.stringify(data)) {
        console.log("⏭️ Pas de changement, skip mise à jour");
        return; // Pas de changement, éviter la mise à jour
      }

      console.log("✅ Mise à jour testData");
      // S'assurer que les données CAT et Conditional sont bien incluses
      this.testData = {
        ...data,
        CATParameters: data.CATParameters || {
          paliers: 4,
          confirmation: 3,
          echec: 3,
          calculatedItems: 0,
          randomizedItemOrder: true,
        },
        CATStructure: data.CATStructure || {},
        CATDistribution: data.CATDistribution || {},
        ConditionalStructure: data.ConditionalStructure || {
          nodes: [],
          edges: [],
          startNode: null,
          terminationNodes: [],
          viewport: { x: 0, y: 0, zoom: 1 }
        },
        levels: data.levels || [], // Nouveau: structure des niveaux CAT
      };

      // Récupérer les éléments CAT en cache après chargement des données
      if (data && data.progression === "adaptive") {
        console.log(
          "🐈 Test CAT détecté, récupération des éléments en cache..."
        );
        this.recoverCachedCATElements();
      }
    },
    getTestData() {
      return this.testData;
    },
    setCurrentTest(test) {
      this.resetState();
      this.currentTest = test;
      this.currentPage = null;
      this.currentElement = null;
    },
    setSelectedElement(element, label) {
      console.log("🎯 setSelectedElement appelé avec:");
      console.log("   - element:", element);
      console.log("   - label:", label);
      console.log("   - element.el_ID:", element?.el_ID);
      console.log("   - element.metadata:", element?.metadata);

      this.selectedElement = element;
      this.selectedElementLabel = label;

      // Pour les éléments CAT temporaires, on ne les ajoute PAS aux testData.elements
      // car ils sont gérés séparément et ne doivent pas interférer avec la synchronisation CAT
      if (element?.metadata?.Source === "CAT_TEMP_EDITABLE") {
        console.log(
          "🎯 CAT temp element selected - will be managed separately from test elements"
        );
        // Ne rien faire ici, l'élément est juste sélectionné pour édition
        // Les modifications seront sauvegardées directement dans l'Item Bank via updateCATElement
      }
    },
    updateElementLabel(newLabel) {
      console.log(newLabel);
      if (!this.testData || !this.selectedElement || this.testData._id === "")
        return;

      // Update the label in pages.children immediately
      this.testData.pages.forEach((page) => {
        const child = page.children.find(
          (child) => child.id === this.selectedElement.el_ID
        );
        if (child) {
          child.label = newLabel;
        }
      });

      this.selectedElementLabel = newLabel;
      localStorage.setItem("testData", JSON.stringify(this.testData));
    },

    addElementFromImport(element, pageID = null) {
      if (!this.testData || !this.testData.elements) return;

      // Ajouter l'élément au tableau elements
      this.testData.elements.push(element);

      // Si un pageID est fourni, ajouter aussi à la structure de pages
      if (pageID && this.testData.pages) {
        const page = this.testData.pages.find((p) => p.id === pageID);
        if (page) {
          const newChild = {
            id: element.el_ID,
            label: element.el_Text || element.el_Label || "Imported Element"
          };
          page.children.push(newChild);
          console.log(`📄 Élément ${element.el_ID} ajouté à la page ${pageID}`);
        }
      }
    },

    updateElement(updatedElement) {
      if (!this.testData || this.testData._id === "") return;

      // Nettoyer les images invalides AVANT la mise à jour
      const cleanedElement = this.cleanInvalidImages({ ...updatedElement });

      console.log("🔧 updateElement - el_ID:", cleanedElement.el_ID);
      console.log(
        "🔍 Elements dans testData:",
        this.testData?.elements?.length || 0,
        "elements"
      );

      // Vérifier si c'est un élément CAT (contient une source de l'Item Bank)
      const isCATElement =
        cleanedElement.metadata?.Source === "CAT_TEMP_EDITABLE" ||
        cleanedElement.metadata?.CreatedFrom === "CAT_DISTRIBUTION" ||
        cleanedElement.metadata?.OriginalItemId;

      //console.log("🔍 isCATElement:", isCATElement, "Source:", cleanedElement.metadata?.Source);

      if (isCATElement) {
        console.log(`🐈 CAT Element update detected: ${cleanedElement.el_ID}`);
        this.updateCATElement(cleanedElement);
        return;
      }

      const elementIndex = this.testData.elements.findIndex(
        (el) => el.el_ID === cleanedElement.el_ID
      );

      console.log(
        "🔍 Element index found:",
        elementIndex,
        "pour el_ID:",
        cleanedElement.el_ID
      );

      let existingElement = null;

      if (elementIndex !== -1) {
        existingElement = this.testData.elements[elementIndex];

        // Vos valeurs par défaut existantes
        const defaultElement = {
          el_ID: "",
          el_Type: "",
          el_Text: "",
          el_RichText: "",
          options: [],
          multiple: false,
          maxScore: "0",
          feedback: {
            correct: "",
            incorrect: "",
          },
          gaps: [],
          el_GapsedText: "",
          labels: [],
          el_InteractionData: {},
          AIisActivated: true,
          correctionAxes: [],
          tip: "",
          randomized: false,
          isNewElement: false,
          language: "en",
          dimension: [],
          difficulty_rating: existingElement.difficulty_rating || 5, // Initialiser si pas défini
          // AJOUT POUR SUPPORT BASE64
          el_data: existingElement.el_data || {}, // Préserver el_data existant
          // AJOUT POUR INTERACTION DRAWING
          drawingConfig: existingElement.drawingConfig || {},
        };

        // Fusionner avec validation des images
        this.testData.elements[elementIndex] = {
          ...defaultElement,
          ...existingElement,
          ...cleanedElement,
        };

        // Log pour debug (optionnel)
        if (cleanedElement.el_data?.backgroundImage?.base64) {
          const imageSize = this.getBase64Size(
            cleanedElement.el_data.backgroundImage.base64
          );
          console.log(
            `Image Base64 sauvegardée pour ${cleanedElement.el_ID}: ${(
              imageSize / 1024
            ).toFixed(1)} KB`
          );
        }
      } else {
        console.warn("Élément non trouvé :", cleanedElement.el_ID);
      }

      localStorage.setItem("testData", JSON.stringify(this.testData));
      this.saveTestDataToDatabase();

      // Synchroniser les difficultés avec la table CAT si l'élément a une difficulté (version debounced)
      // Éviter les syncs si seuls les changements de texte/contenu (pas de difficulté)
      if (
        cleanedElement.difficulty_rating &&
        typeof cleanedElement.difficulty_rating === "number" &&
        existingElement &&
        existingElement.difficulty_rating !== cleanedElement.difficulty_rating
      ) {
        console.log(
          `🎯 Difficulty changed for ${cleanedElement.el_ID}: ${existingElement.difficulty_rating} → ${cleanedElement.difficulty_rating}`
        );
        this.debouncedSyncCAT();
      }
    },

    // Méthode spécialisée pour la mise à jour des éléments CAT
    async updateCATElement(element) {
      console.log(`🐈 Updating CAT element: ${element.el_ID}`);
      console.log(
        `🔍 Current testData ID: ${this.testData?.ID}, _id: ${this.testData?._id}`
      );

      try {
        // Nettoyer les images invalides AVANT la mise à jour
        const cleanedElement = this.cleanInvalidImages({ ...element });

        // Si c'est un élément temporaire CAT, sauvegarder directement dans l'Item Bank
        if (
          cleanedElement.metadata?.Source === "CAT_TEMP_EDITABLE" &&
          cleanedElement.metadata?.OriginalItemId
        ) {
          console.log(
            `💾 Saving CAT temp element to Item Bank: ${cleanedElement.metadata.OriginalItemId}`
          );

          // Sauvegarder dans l'Item Bank via l'API
          const response = await api.put(
            `/items/${cleanedElement.metadata.OriginalItemId}`,
            {
              ...cleanedElement,
              metadata: {
                ...cleanedElement.metadata,
                LastModif: new Date().toISOString(),
                ModifiedViaCAT: true,
              },
            }
          );

          if (response.data) {
            console.log(`✅ CAT element saved to Item Bank successfully`);

            // Émettre un événement pour notifier le CATEditor de la mise à jour
            this.emitCATItemUpdated(cleanedElement);

            // Vérifier si l'élément temporaire existe dans le test local, sinon l'ajouter
            const testElementIndex = this.testData.elements?.findIndex(
              (el) => el.el_ID === cleanedElement.el_ID
            );

            if (testElementIndex !== -1) {
              this.testData.elements[testElementIndex] = { ...cleanedElement };
              console.log(`✅ CAT temp element updated in local test elements`);
            } else {
              // Ajouter l'élément temporaire au test pour la cohérence
              if (!this.testData.elements) {
                this.testData.elements = [];
              }
              this.testData.elements.push({ ...cleanedElement });
              console.log(`✅ CAT temp element added to local test elements`);
            }

            localStorage.setItem("testData", JSON.stringify(this.testData));
            // NE PAS appeler saveTestDataToDatabase() pour les éléments CAT temporaires
            // car cela déclenche la synchronisation CAT qui cause des erreurs
          }
        }
        // Si c'est un élément avec CreatedFrom CAT_DISTRIBUTION, mettre à jour dans le test ET l'Item Bank
        else if (
          cleanedElement.metadata?.CreatedFrom === "CAT_DISTRIBUTION" &&
          cleanedElement.metadata?.SourceItemId
        ) {
          console.log(
            `💾 Updating CAT distribution element in both test and Item Bank`
          );

          // 1. Mettre à jour dans les éléments du test s'il existe
          const testElementIndex = this.testData.elements?.findIndex(
            (el) =>
              el.el_ID === cleanedElement.el_ID ||
              el.metadata?.SourceItemId === cleanedElement.metadata.SourceItemId
          );

          if (testElementIndex !== -1) {
            this.testData.elements[testElementIndex] = { ...cleanedElement };
            localStorage.setItem("testData", JSON.stringify(this.testData));
            // NE PAS appeler saveTestDataToDatabase() pour éviter la synchronisation CAT récursive
            console.log(
              `✅ Updated CAT element in test: ${cleanedElement.el_ID}`
            );
          }

          // 2. Mettre à jour dans l'Item Bank
          try {
            const response = await api.put(
              `/items/${cleanedElement.metadata.SourceItemId}`,
              {
                ...cleanedElement,
                metadata: {
                  ...cleanedElement.metadata,
                  LastModif: new Date().toISOString(),
                  ModifiedViaCAT: true,
                },
              }
            );

            if (response.data) {
              console.log(`✅ CAT element also saved to Item Bank`);
            }
          } catch (itemBankError) {
            console.warn(
              `⚠️ Could not update Item Bank for CAT element:`,
              itemBankError
            );
            // Continue sans faire échouer l'opération
          }
        }
        // Si c'est un élément CAT sans métadonnées spécifiques, essayer de le sauvegarder localement
        else {
          console.log(`💾 Updating standard CAT element in test elements`);

          const testElementIndex = this.testData.elements?.findIndex(
            (el) => el.el_ID === cleanedElement.el_ID
          );

          if (testElementIndex !== -1) {
            // Fusionner avec les valeurs par défaut
            const defaultElement = {
              el_ID: "",
              el_Type: "",
              el_Text: "",
              el_RichText: "",
              options: [],
              multiple: false,
              maxScore: "0",
              feedback: {
                correct: "",
                incorrect: "",
              },
              gaps: [],
              el_GapsedText: "",
              labels: [],
              el_InteractionData: {},
              AIisActivated: false,
              correctionAxes: [],
              tip: "",
              randomized: false,
              isNewElement: false,
              language: "en",
              dimension: [],
              difficulty_rating: 5,
              el_data: {},
            };

            const existingElement = this.testData.elements[testElementIndex];

            this.testData.elements[testElementIndex] = {
              ...defaultElement,
              ...existingElement,
              ...cleanedElement,
            };

            localStorage.setItem("testData", JSON.stringify(this.testData));
            // NE PAS appeler saveTestDataToDatabase() pour éviter la synchronisation CAT récursive
            console.log(
              `✅ CAT element updated in test elements: ${cleanedElement.el_ID}`
            );
          } else {
            console.warn(
              `⚠️ CAT element ${cleanedElement.el_ID} not found in test elements`
            );
          }
        }

        // NE PAS synchroniser les difficultés CAT pour les éléments temporaires
        // car cela cause des erreurs "test is not defined"
        if (
          cleanedElement.difficulty_rating &&
          typeof cleanedElement.difficulty_rating === "number"
        ) {
          console.log(
            `🎯 CAT difficulty noted: ${cleanedElement.difficulty_rating} (not syncing for temp elements)`
          );
          // this.debouncedSyncCAT(); // Désactivé pour les éléments CAT temporaires
        }
      } catch (error) {
        console.error(`❌ Error updating CAT element:`, error);
        // En cas d'erreur, stocker temporairement dans le localStorage
        const catElements = JSON.parse(
          localStorage.getItem("catElementsCache") || "{}"
        );
        catElements[element.el_ID] = {
          ...element,
          lastModified: Date.now(),
          pendingSync: true,
        };
        localStorage.setItem("catElementsCache", JSON.stringify(catElements));
        console.log(`📦 CAT element cached locally for later sync`);
      }
    },

    // Méthode pour récupérer et synchroniser les éléments CAT en cache
    async recoverCachedCATElements() {
      try {
        const cachedElements = JSON.parse(
          localStorage.getItem("catElementsCache") || "{}"
        );
        const elementIds = Object.keys(cachedElements);

        if (elementIds.length === 0) {
          return;
        }

        console.log(`🔄 Recovering ${elementIds.length} cached CAT elements`);

        for (const elementId of elementIds) {
          const cachedElement = cachedElements[elementId];

          if (cachedElement.pendingSync) {
            console.log(`🔄 Syncing cached CAT element: ${elementId}`);

            try {
              await this.updateCATElement(cachedElement);

              // Supprimer de cache après synchronisation réussie
              delete cachedElements[elementId];
              console.log(
                `✅ Cached CAT element ${elementId} synced and removed from cache`
              );
            } catch (error) {
              console.error(
                `❌ Failed to sync cached CAT element ${elementId}:`,
                error
              );
              // Marquer comme échoué mais garder en cache pour retry ultérieur
              cachedElement.syncFailed = true;
              cachedElement.lastSyncAttempt = Date.now();
            }
          }
        }

        // Mettre à jour le cache après nettoyage
        localStorage.setItem(
          "catElementsCache",
          JSON.stringify(cachedElements)
        );

        const remainingCount = Object.keys(cachedElements).length;
        if (remainingCount > 0) {
          console.log(
            `⚠️ ${remainingCount} CAT elements remain in cache due to sync failures`
          );
        } else {
          console.log(`✅ All cached CAT elements successfully synced`);
        }
      } catch (error) {
        console.error("❌ Error recovering cached CAT elements:", error);
      }
    },

    // Méthode pour émettre un événement de mise à jour d'item CAT
    emitCATItemUpdated(element) {
      try {
        // Créer un événement personnalisé
        const event = new CustomEvent("cat-item-updated", {
          detail: {
            itemId: element.metadata?.OriginalItemId || element._id,
            updatedElement: element,
            newTitle: element.el_Text,
            newPreview:
              element.el_Text?.substring(0, 100) +
              (element.el_Text?.length > 100 ? "..." : ""),
            timestamp: Date.now(),
          },
        });

        // Émettre sur window pour que tous les composants puissent l'écouter
        window.dispatchEvent(event);
        console.log(
          "📡 CAT item update event emitted for:",
          element.metadata?.OriginalItemId
        );
      } catch (error) {
        console.error("❌ Error emitting CAT item update event:", error);
      }
    },

    removeElement(el_ID) {
      if (!this.testData || !this.testData.pages || !this.testData.elements) {
        console.error("Test data or pages/elements are not initialized.");
        return;
      }

      // Supprimer l'élément de la liste globale des éléments
      const elementIndex = this.testData.elements.findIndex(
        (el) => el.el_ID === el_ID
      );
      if (elementIndex !== -1) {
        this.testData.elements.splice(elementIndex, 1);
        console.log(`Element with ID ${el_ID} removed from elements.`);
      } else {
        console.warn(`Element with ID ${el_ID} not found in elements.`);
      }

      // Supprimer les références de l'élément dans les pages
      this.testData.pages.forEach((page) => {
        const initialLength = page.children.length;
        page.children = page.children.filter((child) => child.id !== el_ID);
        if (page.children.length < initialLength) {
          console.log(`Element with ID ${el_ID} removed from page ${page.id}.`);
        }
      });

      // Sauvegarder les modifications dans localStorage
      localStorage.setItem("testData", JSON.stringify(this.testData));
    },

    // Gestion des pages et des éléments
    addPage(newPage) {
      if (!this.testData || !this.testData.pages) {
        console.error("Test data or pages are not initialized.");
        return;
      }

      // Ajout de la nouvelle page
      this.testData.pages.push({
        ...newPage,
        Randomized: false,
      });

      // Sauvegarde dans localStorage pour persistance
      localStorage.setItem("testData", JSON.stringify(this.testData));
    },

    addElement(pageID, elementID, customData = null) {
      if (!this.testData || !this.testData.pages) {
        console.error("Test data or pages are not initialized.");
        return;
      }

      // Ajout dans la page identifiée par pageID
      const page = this.testData.pages.find((p) => p.id === pageID);
      if (!page) {
        console.error(`Page with ID ${pageID} not found.`);
        return;
      }

      // Ajoute un enfant à la page
      const newChild = {
        id: elementID,
        label: "New Element", // Nom par défaut pour le child
      };
      page.children.push(newChild);

      // Création des données de l'élément avec des valeurs par défaut
      const defaultElement = {
        el_ID: elementID,
        el_Type: "default",
        el_Text: "Here the main title - question or message",
        el_RichText: "This is some instruction with Rich Text",
        options: [
          {
            id: "choice1" + elementID,
            text: "Choice 1",
            isCorrect: false,
          },
          {
            id: "choice2" + elementID,
            text: "Choice 2",
            isCorrect: true,
            weight: 1,
          },
        ],
        maxScore: "1",
        feedback: {
          correct: "Correct !",
          incorrect: "Incorrect",
        },
        tip: "This is a hint",
        isNewElement: false,
        labels: ["Label 1", "Label 2", "Label 3"],
        dimension: [],
        difficulty_rating: 5, // Initialiser automatiquement la difficulté par défaut
        // Héritage des métadonnées depuis le test parent
        metadata: {
          Author: this.testData?.metadata?.Author || "",
          Institution: "",
          Created: new Date().toISOString(),
          LastModif: new Date().toISOString(),
          CreatedFrom: "",
          Version: "",
          Subject: this.testData?.Subject || "", // Code UNESCO hérité du test
          ISCED: this.testData?.ISCED || "", // Code ISCED hérité du test
        },
      };

      // Fusion avec les données personnalisées si fournies
      const newElement = customData
        ? { ...defaultElement, ...customData }
        : defaultElement;

      // Initialisation du tableau global des éléments s'il n'existe pas
      if (!this.testData.elements) {
        this.testData.elements = [];
      }

      // Ajout de l'élément dans le tableau global
      this.testData.elements.push(newElement);

      // Sauvegarde dans localStorage
      localStorage.setItem("testData", JSON.stringify(this.testData));
    },

    deletePage(pageId) {
      if (!this.testData || !this.testData.pages) {
        console.error("Test data or pages are not initialized.");
        return;
      }

      // Supprime la page par ID
      this.testData.pages = this.testData.pages.filter(
        (page) => page.id !== pageId
      );

      // Sauvegarde dans localStorage pour persistance
      localStorage.setItem("testData", JSON.stringify(this.testData));
    },

    deleteElement(elementId, fromNode = false) {
      console.log("🗑️ Suppression de l'élément:", elementId);

      if (!this.testData || !this.testData.pages) {
        console.error("Test data or pages are not initialized.");
        return false;
      }

      // Vérifier si l'élément existe
      const elementExists = this.testData.elements?.some(
        (el) => el.el_ID === elementId
      );
      if (!elementExists) {
        console.warn(`⚠️ Element with ID ${elementId} not found in elements.`);
        return false;
      }

      // Parcourt toutes les pages pour trouver et supprimer l'élément
      this.testData.pages.forEach((page) => {
        const initialLength = page.children.length;
        page.children = page.children.filter((child) => child.id !== elementId);
        if (page.children.length < initialLength) {
          console.log(
            `✅ Element removed from page "${page.label}" (${page.id})`
          );
        }
      });

      // Supprimer également l'élément de la liste globale des éléments
      if (this.testData.elements) {
        const initialLength = this.testData.elements.length;
        this.testData.elements = this.testData.elements.filter(
          (el) => el.el_ID !== elementId
        );
        if (this.testData.elements.length < initialLength) {
          console.log(`✅ Element removed from global elements list`);
        }
      }

      // Si l'élément supprimé était sélectionné, désélectionner
      if (this.selectedElement?.el_ID === elementId) {
        console.log("🔄 Clearing selected element");
        this.clearSelectedElement();
      }

      // Sauvegarde dans localStorage et base de données
      localStorage.setItem("testData", JSON.stringify(this.testData));
      this.saveTestDataToDatabase();

      console.log(`✅ Element ${elementId} successfully deleted`);
      return true;
    },

    clearSelectedElement() {
      this.selectedElement = null;
      this.selectedElementLabel = null;
    },
    updatePageOrder(pageId, newOrder) {
      const page = this.testData.pages.find((p) => p.id === pageId);
      if (page) {
        page.children = newOrder;
        localStorage.setItem("testData", JSON.stringify(this.testData)); // Sauvegarde dans localStorage
        this.saveTestDataToDatabase(); // Sauvegarde dans MongoDB
      }
    },

    updatePagesOrder(newPagesOrder) {
      this.testData.pages = newPagesOrder;
      localStorage.setItem("testData", JSON.stringify(this.testData));
      this.saveTestDataToDatabase();
      console.log(
        "✅ Ordre des pages mis à jour:",
        newPagesOrder.map((p) => p.label)
      );
    },
    deleteTestById(id) {
      console.log(`Suppression du test avec ID : ${id}`);

      // Vérifiez si l'ID correspond au test actuel
      if (this.testData.ID === id) {
        // Supprimer complètement les données locales
        this.testData = null; // Supprime l'objet test

        console.log(`Test avec ID ${id} supprimé du store.`);

        // Supprimer également du localStorage
        localStorage.removeItem("testData");

        console.log(`Test avec ID ${id} supprimé de localStorage.`);
      } else {
        console.error(
          `Test avec ID ${id} non trouvé dans les données locales.`
        );
      }
    },

    getElementPage(el_ID) {
      if (!this.testData || !this.testData.pages) {
        console.error("Test data or pages are not initialized.");
        return null;
      }

      // Parcourt toutes les pages pour trouver celle qui contient l'élément
      const page = this.testData.pages.find((page) =>
        page.children.some((child) => child.id === el_ID)
      );

      if (page) {
        return page; // Retourne la page si trouvée
      } else {
        console.warn(`Element with ID ${el_ID} not found in any page.`);
        return null; // Retourne null si l'élément n'est pas trouvé
      }
    },
    getActivePageId() {
      const activeElement = this.selectedElement;
      if (!activeElement || !activeElement.el_ID) return null;

      const page = this.testData.pages.find((p) =>
        p.children.some((child) => child.id === activeElement.el_ID)
      );
      return page ? page.id : null;
    },

    //Image Base64 Validation

    validateBase64Image(base64String) {
      try {
        if (!base64String || typeof base64String !== "string") return false;

        // Vérifier le format data:image/...;base64,
        const dataUrlPattern = /^data:image\/(png|jpeg|jpg|gif|webp);base64,/;
        if (!dataUrlPattern.test(base64String)) return false;

        // Vérifier que la partie Base64 est valide
        const base64Data = base64String.split(",")[1];
        if (!base64Data) return false;

        // Test de décodage Base64 simple
        try {
          atob(base64Data);
          return true;
        } catch {
          return false;
        }
      } catch (error) {
        console.error("Erreur validation Base64:", error);
        return false;
      }
    },

    //Calcule la taille d'une image Base64 en bytes
    getBase64Size(base64String) {
      if (!base64String) return 0;
      const base64Data = base64String.split(",")[1] || base64String;
      return Math.round(base64Data.length * 0.75);
    },

    /**
     * Nettoie les images invalides d'un élément
     */
    cleanInvalidImages(element) {
      if (!element.el_data?.backgroundImage?.base64) return element;

      if (!this.validateBase64Image(element.el_data.backgroundImage.base64)) {
        console.warn(
          `Image Base64 invalide dans l'élément ${element.el_ID}, suppression...`
        );
        delete element.el_data.backgroundImage;
        this.imageValidationErrors.push({
          elementId: element.el_ID,
          error: "Image Base64 invalide supprimée",
          timestamp: new Date().toISOString(),
        });
      }

      return element;
    },
    /**
     * Exporte un test avec toutes les images intégrées en Base64
     */
    async exportTestWithImages() {
      try {
        // Nettoyer toutes les images avant export
        const cleanedElements = this.testData.elements.map((element) =>
          this.cleanInvalidImages({ ...element })
        );

        const testData = {
          ...this.testData,
          elements: cleanedElements,
          exportMetadata: {
            exportDate: new Date().toISOString(),
            version: "1.0",
            containsImages: cleanedElements.some(
              (el) => el.el_data?.backgroundImage?.base64
            ),
            imageCount: cleanedElements.filter(
              (el) => el.el_data?.backgroundImage?.base64
            ).length,
            totalImageSize: cleanedElements.reduce((total, el) => {
              return (
                total +
                (el.el_data?.backgroundImage?.base64
                  ? this.getBase64Size(el.el_data.backgroundImage.base64)
                  : 0)
              );
            }, 0),
          },
        };

        console.log("Export préparé:", {
          elements: testData.elements.length,
          images: testData.exportMetadata.imageCount,
          totalSize: `${(
            testData.exportMetadata.totalImageSize /
            1024 /
            1024
          ).toFixed(2)} MB`,
        });

        return testData;
      } catch (error) {
        console.error("Erreur lors de l'export:", error);
        throw new Error("Échec de l'export du test");
      }
    },

    /**
     * Importe un test avec images Base64
     */
    async importTestWithImages(testData) {
      try {
        // Validation et nettoyage des images lors de l'import
        if (testData.elements) {
          testData.elements = testData.elements.map((element, index) => {
            if (element.el_data?.backgroundImage?.base64) {
              if (
                !this.validateBase64Image(
                  element.el_data.backgroundImage.base64
                )
              ) {
                console.warn(
                  `Image invalide dans l'élément ${index} (${element.el_ID}), suppression...`
                );
                delete element.el_data.backgroundImage;
                this.imageValidationErrors.push({
                  elementId: element.el_ID,
                  error: "Image invalide supprimée lors de l'import",
                  timestamp: new Date().toISOString(),
                });
              }
            }
            return element;
          });
        }

        // Mise à jour du store avec les données importées
        this.testData = { ...testData };

        const importStats = {
          elementsCount: this.testData.elements.length,
          imagesCount: this.testData.elements.filter(
            (el) => el.el_data?.backgroundImage?.base64
          ).length,
          errorsCount: this.imageValidationErrors.length,
        };

        console.log("Test importé avec succès:", importStats);

        // Sauvegarder après import
        await this.saveTestDataToDatabase();

        return importStats;
      } catch (error) {
        console.error("Erreur lors de l'import:", error);
        throw new Error("Échec de l'import du test");
      }
    },

    /**
     * Récupère les statistiques des images du test actuel
     */
    getImageStatistics() {
      if (!this.testData?.elements) return null;

      const elementsWithImages = this.testData.elements.filter(
        (el) => el.el_data?.backgroundImage?.base64
      );

      const totalSize = elementsWithImages.reduce((total, el) => {
        return total + this.getBase64Size(el.el_data.backgroundImage.base64);
      }, 0);

      return {
        totalElements: this.testData.elements.length,
        elementsWithImages: elementsWithImages.length,
        totalImageSize: totalSize,
        averageImageSize:
          elementsWithImages.length > 0
            ? totalSize / elementsWithImages.length
            : 0,
        totalImageSizeMB: totalSize / 1024 / 1024,
        validationErrors: this.imageValidationErrors.length,
      };
    },

    /**
     * Nettoie le cache et les erreurs de validation
     */
    clearImageCache() {
      this.imageCache.clear();
      this.imageValidationErrors = [];
    },

    // Synchroniser les difficultés avec la table CAT
    async syncDifficultiesWithCAT() {
      // Guard contre les appels récursifs
      if (this._syncInProgress) {
        console.log("⏭️ CAT sync already in progress, skipping...");
        return;
      }

      if (
        !this.testData?.ID ||
        !this.testData?.elements ||
        this.testData.elements.length === 0
      ) {
        return; // Pas de test ID ou pas d'éléments à synchroniser
      }

      // Filtrer seulement les éléments avec une difficulté définie
      const elementsWithDifficulty = this.testData.elements.filter(
        (element) =>
          element.difficulty_rating &&
          typeof element.difficulty_rating === "number"
      );

      if (elementsWithDifficulty.length === 0) {
        return; // Pas d'éléments avec difficulté à synchroniser
      }

      // Marquer la synchronisation comme en cours
      this._syncInProgress = true;

      try {
        console.log(
          `🔄 Syncing ${elementsWithDifficulty.length} element difficulties with CAT table`
        );

        const response = await api.post("/cat/sync-test-difficulties", {
          testId: this.testData.ID,
          testMetadata: {
            ISCED: this.testData.ISCED,
            subject: this.testData.Subject,
            keywords: this.testData.Keywords,
            domain: this.testData.domain,
            level: this.testData.level,
          },
          elements: elementsWithDifficulty.map((element) => ({
            el_ID: element.el_ID,
            el_Type: element.el_Type,
            el_Text: element.el_Text,
            difficulty_rating: element.difficulty_rating,
          })),
        });

        if (response.data.success) {
          console.log(
            `✅ CAT sync completed: ${response.data.synced} updated, ${response.data.created} created`
          );
        } else {
          console.warn("⚠️ CAT sync failed:", response.data.error);
        }
      } catch (error) {
        console.error("❌ Error syncing difficulties with CAT:", error);
        // Ne pas interrompre le processus de sauvegarde pour cette erreur
      } finally {
        // Réinitialiser le flag après un délai pour éviter les conflits
        setTimeout(() => {
          this._syncInProgress = false;
        }, 1000);
      }
    },

    // Version debounced de la synchronisation CAT pour éviter les appels répétitifs
    debouncedSyncCAT: debounce(function () {
      // Double protection contre les appels multiples
      if (!this._syncInProgress) {
        console.log("🔄 Debounced CAT sync triggered");
        this.syncDifficultiesWithCAT();
      } else {
        console.log("⏭️ CAT sync already in progress, skipping debounced call");
      }
    }, 2000), // Délai de 2 secondes pour éviter les appels répétitifs

    /**
     * Initialise la structure testData.levels pour un test CAT
     */
    initializeCATLevels(catParameters = null) {
      const params = catParameters ||
        this.testData.CATParameters || {
          paliers: 4,
          randomizedItemOrder: true,
        };
      const numLevels = params.paliers || 4;

      console.log(
        "🆕 Initializing testData.levels for CAT test with",
        numLevels,
        "levels"
      );

      // Fonction pour générer un UUID simple (sans dépendance externe)
      const generateId = () => {
        return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
          /[xy]/g,
          function (c) {
            const r = (Math.random() * 16) | 0;
            const v = c === "x" ? r : (r & 0x3) | 0x8;
            return v.toString(16);
          }
        );
      };

      // Créer la structure initiale des niveaux (similaire aux pages)
      const initialLevels = Array.from({ length: numLevels }, (_, index) => ({
        _id: generateId(),
        label: `Level ${index + 1}`,
        items: [], // Array d'el_ID vide
        randomized: params.randomizedItemOrder || true,
      }));

      // Mettre à jour testData.levels
      this.testData.levels = initialLevels;

      console.log(
        "✅ testData.levels initialized:",
        initialLevels.map((l) => l.label).join(", ")
      );

      // Sauvegarder immédiatement
      this.saveTestDataToDatabase();

      return initialLevels;
    },

    /**
     * Met à jour la structure testData.levels
     */
    updateCATLevels(levelsData) {
      if (!Array.isArray(levelsData)) {
        console.error("❌ levelsData must be an array");
        return;
      }

      console.log(
        "🔄 Updating testData.levels with",
        levelsData.length,
        "levels"
      );

      this.testData.levels = levelsData;

      // Sauvegarder automatiquement
      this.saveTestDataToDatabase();

      console.log("✅ testData.levels updated successfully");
    },

    // 🎛️ ===== GESTION DES PARAMÈTRES DE PAGE =====

    /**
     * Applique des paramètres à une ou plusieurs pages selon la portée
     * @param {Object} payload - { parameters, scope, pageId }
     */
    applyPageParameters({ parameters, scope, pageId }) {
      console.log("🎛️ Application des paramètres de page:", {
        parameters,
        scope,
        pageId,
      });

      if (!this.testData.pages || !Array.isArray(this.testData.pages)) {
        console.error("❌ Pas de pages disponibles dans testData");
        return;
      }

      // Appliquer les paramètres selon la portée
      this.testData.pages = applyParametersToPages(
        this.testData.pages,
        parameters,
        scope,
        pageId
      );

      // Sauvegarder automatiquement
      this.saveTestDataToDatabase();

      console.log("✅ Paramètres de page appliqués et sauvegardés");
    },

    /**
     * Met à jour les paramètres d'une page spécifique
     * @param {String} pageId - ID de la page
     * @param {Object} parameters - Nouveaux paramètres
     */
    updatePageParameters(pageId, parameters) {
      console.log("🎛️ Mise à jour des paramètres pour la page:", pageId);

      const pageIndex = this.testData.pages.findIndex((p) => p.id === pageId);
      if (pageIndex === -1) {
        console.error("❌ Page non trouvée:", pageId);
        return;
      }

      // Initialiser parameters si nécessaire
      if (!this.testData.pages[pageIndex].parameters) {
        this.testData.pages[pageIndex].parameters = {};
      }

      // Fusionner avec les paramètres existants
      this.testData.pages[pageIndex].parameters = {
        ...this.testData.pages[pageIndex].parameters,
        ...parameters,
      };

      // Sauvegarder
      this.saveTestDataToDatabase();

      console.log(
        "✅ Paramètres de page mis à jour:",
        this.testData.pages[pageIndex].parameters
      );
    },

    /**
     * Applique les mêmes paramètres à toutes les pages
     * @param {Object} parameters - Paramètres à appliquer
     */
    applyParametersToAllPages(parameters) {
      console.log("🌐 Application des paramètres à toutes les pages");

      this.testData.pages.forEach((page) => {
        if (!page.parameters) {
          page.parameters = {};
        }
        page.parameters = {
          ...page.parameters,
          ...parameters,
        };
      });

      this.saveTestDataToDatabase();
      console.log("✅ Paramètres appliqués à toutes les pages");
    },

    /**
     * Remet les paramètres d'une page aux valeurs par défaut
     * @param {String} pageId - ID de la page
     */
    resetPageParameters(pageId) {
      console.log("🔄 Remise à zéro des paramètres pour la page:", pageId);

      const pageIndex = this.testData.pages.findIndex((p) => p.id === pageId);
      if (pageIndex === -1) {
        console.error("❌ Page non trouvée:", pageId);
        return;
      }

      // Supprimer les paramètres personnalisés (retour aux défauts)
      delete this.testData.pages[pageIndex].parameters;

      this.saveTestDataToDatabase();
      console.log("✅ Paramètres de page remis à zéro");
    },

    // ======== NODES MANAGEMENT FOR CONDITIONAL FLOW ========

setConditionalStructure(structure) {
  // structure = { nodes, edges }
  this.ConditionalStructure = structure
},

setConditionalNodes(nodes) {
  this.ConditionalStructure.nodes = nodes
},

addConditionalNode(node) {
  if (!this.ConditionalStructure.nodes) this.ConditionalStructure.nodes = []
  this.ConditionalStructure.nodes.push(node)
  console.log('🟢 Node ajouté au ConditionalStructure:', node.id)
},

updateConditionalNodeData(nodeId, newData) {
  const index = this.ConditionalStructure.nodes.findIndex(n => n.id === nodeId)
  if (index !== -1) {
    this.ConditionalStructure.nodes[index].data = {
      ...this.ConditionalStructure.nodes[index].data,
      ...newData,
    }
    console.log(`✏️ Node ${nodeId} mis à jour dans ConditionalStructure`)
  }
},

removeConditionalNode(nodeId) {
  this.ConditionalStructure.nodes = this.ConditionalStructure.nodes.filter(n => n.id !== nodeId)
  this.ConditionalStructure.edges = this.ConditionalStructure.edges.filter(
    e => e.source !== nodeId && e.target !== nodeId
  )
  console.log(`🗑️ Node ${nodeId} supprimé du ConditionalStructure`)
},

// ======== EDGES MANAGEMENT ========

addConditionalEdge(edge) {
  if (!this.ConditionalStructure.edges) this.ConditionalStructure.edges = []
  this.ConditionalStructure.edges.push(edge)
},

setConditionalEdges(edges) {
  this.ConditionalStructure.edges = edges
},

removeConditionalEdge(edgeId) {
  this.ConditionalStructure.edges = this.ConditionalStructure.edges.filter(e => e.id !== edgeId)
},


  },

  getters: {
    getElementById: (state) => (id) => {
      console.log("🔍 getElementById appelé pour:", id);
      console.log("🔍 testData disponible:", !!state.testData);
      console.log(
        "🔍 elements disponibles:",
        state.testData?.elements?.length || 0
      );

      if (!state.testData) {
        console.log("❌ Pas de testData disponible");
        return null;
      }

      const element = state.testData.elements.find((el) => el.el_ID === id);
      console.log("🔍 Element trouvé:", !!element);

      if (!element) {
        console.log("❌ Élément non trouvé :", id);
        console.log(
          "📋 Elements disponibles:",
          state.testData.elements.map((el) => el.el_ID)
        );
      } else {
        console.log("✅ Element trouvé:", {
          el_ID: element.el_ID,
          el_Type: element.el_Type,
        });
      }

      return element;
    },
    getElementLabel: (state) => (id) => {
      if (!state.testData) return null;
      let label = null;
      state.testData.pages.some((page) => {
        const child = page.children.find((child) => child.id === id);
        if (child) {
          label = child.label;
          return true;
        }
        return false;
      });
      return label;
    },

    //Récupère les éléments contenant des images
    elementsWithImages: (state) => {
      if (!state.testData?.elements) return [];
      return state.testData.elements.filter(
        (el) => el.el_data?.backgroundImage?.base64
      );
    },
    // Vérifie si un élément a une image valide
    hasValidImage: (state) => (elementId) => {
      const element = state.testData?.elements?.find(
        (el) => el.el_ID === elementId
      );
      if (!element?.el_data?.backgroundImage?.base64) return false;

      // Utiliser la méthode de validation (on doit la rendre accessible)
      const dataUrlPattern = /^data:image\/(png|jpeg|jpg|gif|webp);base64,/;
      return dataUrlPattern.test(element.el_data.backgroundImage.base64);
    },

    // 🎛️ Récupère les paramètres effectifs d'une page (avec fusion des défauts)
    getPageParameters: (state) => (pageId) => {
      const page = state.testData?.pages?.find((p) => p.id === pageId);
      if (!page) {
        console.warn("⚠️ Page non trouvée:", pageId);
        return DEFAULT_PAGE_PARAMETERS;
      }
      return getEffectivePageParameters(page, DEFAULT_PAGE_PARAMETERS);
    },

    // 🎛️ Récupère une page par son ID
    getPageById: (state) => (pageId) => {
      return state.testData?.pages?.find((p) => p.id === pageId) || null;
    },

    // 🎛️ Récupère l'index d'une page
    getPageIndex: (state) => (pageId) => {
      return state.testData?.pages?.findIndex((p) => p.id === pageId) ?? -1;
    },
  },
});
